import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Paper,
  CardActionArea,
  CardContent,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import LockIcon from "@mui/icons-material/Lock";
import ListIcon from "@mui/icons-material/List";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ThemeContext } from '../context/ThemeContext';
import CreateCustomRolePanel from "./Panels/CreateCustomRolePanel";
import ListsPanel from "./Panels/ListsPanel";
import SetPermissionsPanel from "./Panels/SetPermissionsPanel";
import RegisterUserPanel from "./Panels/RegisterUserPanel";
import MyPermissionsPanel from "./Panels/MyPermissionsPanel";
import MyDetailsPanel from "./Panels/MyDetailsPanel";
import ClassIcon from "@mui/icons-material/Class";
import ClassPanel from "./Panels/ClassPanel";
import FolderIcon from "@mui/icons-material/Folder";
import ManageFoldersPanel from "./Panels/ManageFoldersPanel";
import { IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Styled Paper
const StyledPaper = styled(Paper)(({ theme, mode, customColors }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
  backgroundColor:
    mode === "dark"
      ? "#2A2A3C"
      : mode === "custom"
        ? customColors?.background || "#f5f5f5"
        : "#f0f7ff",
  borderRadius: "8px",
  boxShadow:
    mode === "dark"
      ? "0 2px 8px rgba(0,0,0,0.6)"
      : "0 2px 8px rgba(0,0,0,0.15)",
}));

// Palette setup
const palette = {
  light: {
    headerBg: "#e3f2fd",
    headerText: "#37474f",
    rowHover: "#f5f5f5",
    text: "#333",
  },
  dark: {
    headerBg: "#2A2A3C",
    headerText: "#EDEDED",
    rowHover: "#383C54",
    text: "#EDEDED",
  },
  custom: (customColors) => ({
    headerBg: customColors?.cardBg || "#f5f5f5",
    headerText: customColors?.text || "#111111",
    rowHover: customColors?.primary || customColors?.primary || "#e8f4ff",
    text: customColors?.text || "#111111",
  }),
};

export default function ControlPanel() {
  const [scrollPosition, setScrollPosition] = useState(0);
const scrollContainerRef = React.useRef(null);
  const [selectedPanel, setSelectedPanel] = useState("");
  const [customRoles, setCustomRoles] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [myPermissions, setMyPermissions] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { mode, customColors } = useContext(ThemeContext);

  // Fetch current user role from session storage on component mount
  React.useEffect(() => {
    const role = sessionStorage.getItem("userRole");
    if (role) {
      setCurrentUserRole(role);
    }
  }, []);

  const handleScroll = (direction) => {
  const container = scrollContainerRef.current;
  if (container) {
    const cardWidth = 170; // minWidth of each card
    const gap = 12; // gap between cards (1.5 * 8 = 12px from theme spacing)
    const scrollAmount = cardWidth + gap;
    
    const newPosition = direction === 'left' 
      ? Math.max(0, scrollPosition - scrollAmount)
      : scrollPosition + scrollAmount;
    
    container.scrollTo({ left: newPosition, behavior: 'smooth' });
    setScrollPosition(newPosition);
  }
};

  // Snackbar handlers
  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: "", severity: "info" });
  };

  // Handle panel selection change
  const handlePanelChange = (panel) => () => {
    // Toggle: if clicking same panel, close it; otherwise open new panel
    if (selectedPanel === panel) {
      setSelectedPanel("");
    } else {
      setSelectedPanel(panel);
    }
  };

  return (
    <Box sx={{ p: 3, mx: "auto" }}>
      <Box
        sx={{
          position: "sticky",
          top: 64,
          zIndex: 9,
          width: "100%",
          backgroundColor: mode === "dark"
            ? "#000000"
            : mode === "custom"
              ? customColors?.background || "#f5f5f5"
              : "#f5f5f5",
          py: 1,
        }}
      >
       <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
  {/* Left Arrow */}
  <IconButton 
  onClick={() => handleScroll('left')}
  sx={{ 
    position: 'absolute', 
    left: 30,  // Changed from 10 to 0 to bring closer
    zIndex: 10,
    borderRadius: 3,  // Matches card borderRadius
    border: mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(30, 139, 175, 0.2)",
    background: mode === "dark"
      ? "linear-gradient(135deg, #2A2A3C 0%, #1a1a2e 100%)"
      : mode === "custom"
        ? `linear-gradient(135deg, ${customColors?.background || "#ffffff"} 0%, ${customColors?.cardBg || "#f8f9fa"} 100%)`
        : "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
    boxShadow: mode === "dark"
      ? "0 2px 8px rgba(0, 0, 0, 0.3)"
      : "0 2px 12px rgba(30, 139, 175, 0.08)",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    '&:hover': { 
      transform: "translateY(-2px) scale(1.05)",
      boxShadow: mode === "dark"
        ? "0 8px 24px rgba(30, 139, 175, 0.3)"
        : "0 8px 24px rgba(30, 139, 175, 0.2)",
      borderColor: "rgba(30, 139, 175, 0.4)",
    }
  }}
>
    <ChevronLeftIcon />
  </IconButton>

  {/* Scrollable Container */}
  <Box
  ref={scrollContainerRef}
  sx={{
    display: 'flex',
    gap: 1.5,
    overflowX: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    px: 5,  // Increased padding for arrow space
    py: 1,
    maxWidth: 'calc(5 * 180px + 4 * 12px)',  // Show exactly 5 cards (5 cards × 180px + 4 gaps × 12px)
    mx: 'auto',  // Center the container
  }}
>
          {[
            { key: "create_custom_role", icon: <AddCircleOutlineIcon sx={{ fontSize: 30, mb: 0.5 }} />, label: "Create Custom Role" },
            { key: "lists", icon: <ListIcon sx={{ fontSize: 30, mb: 0.5 }} />, label: "Lists" },
            { key: "permissions_for_custom_role", icon: <LockIcon sx={{ fontSize: 30, mb: 0.5 }} />, label: "Set Permissions" },
            { key: "register_user", icon: <GroupIcon sx={{ fontSize: 30, mb: 0.5 }} />, label: "Register User" },
             ...(currentUserRole === "admin" ? [{ key: "classes", icon: <ClassIcon sx={{ fontSize: 30, mb: 0.5 }} />, label: "Classes" }] : []),
             ...(currentUserRole === "admin" ? [ { key: "manage_folders", icon: <FolderIcon sx={{ fontSize: 30, mb: 0.5 }} />, label: "Manage Folders" }] : []),
            ...(currentUserRole !== "admin" ? [{ key: "my_permissions_custom", icon: <LockIcon sx={{ fontSize: 30, mb: 0.5 }} />, label: "My Permissions" }] : []),
            { key: "my_details", icon: <PersonIcon sx={{ fontSize: 30, mb: 0.5 }} />, label: "My Details" },
          ].map((item) => (
<Box key={item.key} sx={{ minWidth: 170, width: 170, flexShrink: 0 }}>  
              <Card
                sx={{
                  borderRadius: 3,
                  border: selectedPanel === item.key
                    ? "2px solid transparent"
                    : mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(30, 139, 175, 0.2)",
                  background: selectedPanel === item.key
                    ? "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)"
                    : mode === "dark"
                      ? "linear-gradient(135deg, #2A2A3C 0%, #1a1a2e 100%)"
                      : mode === "custom"
                        ? `linear-gradient(135deg, ${customColors?.background || "#ffffff"} 0%, ${customColors?.cardBg || "#f8f9fa"} 100%)`
                        : "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                  color: selectedPanel === item.key ? "white" : "inherit",
                  boxShadow: selectedPanel === item.key
                    ? "0 8px 24px rgba(30, 58, 138, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                    : mode === "dark"
                      ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                      : "0 2px 12px rgba(30, 139, 175, 0.08)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": selectedPanel === item.key ? {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: "linear-gradient(90deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.5) 100%)",
                  } : {},
                  "&:hover": {
                    transform: "translateY(-6px) scale(1.02)",
                    boxShadow: selectedPanel === item.key
                      ? "0 12px 32px rgba(30, 58, 138, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                      : mode === "dark"
                        ? "0 8px 24px rgba(30, 139, 175, 0.3)"
                        : "0 8px 24px rgba(30, 139, 175, 0.2)",
                    borderColor: selectedPanel === item.key
                      ? "transparent"
                      : "rgba(30, 139, 175, 0.4)",
                  },
                  "&:active": {
                    transform: "translateY(-4px) scale(1.01)",
                  },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <CardActionArea
                  onClick={handlePanelChange(item.key)}
                  sx={{
                    height: "100%",
                    "&:hover .MuiCardActionArea-focusHighlight": {
                      opacity: 0,
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: "center", p: 2, position: "relative" }}>
                    <Box
                      sx={{
                        color: selectedPanel === item.key ? "white" : mode === "dark" ? "#1e8bafff" : "#1e3a8a",
                        mb: 1,
                        transition: "transform 0.3s ease",
                        transform: selectedPanel === item.key ? "scale(1.1)" : "scale(1)",
                        filter: selectedPanel === item.key ? "drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))" : "none",
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        color: selectedPanel === item.key ? "white" : "inherit",
                        letterSpacing: "0.3px",
                        textShadow: selectedPanel === item.key ? "0 1px 2px rgba(0, 0, 0, 0.2)" : "none",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
          ))}
        </Box>
        <IconButton 
  onClick={() => handleScroll('right')}
  sx={{ 
    position: 'absolute', 
    right: 30,  // Changed from 10 to 0 to bring closer
    zIndex: 10,
    borderRadius: 3,  // Matches card borderRadius
    border: mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(30, 139, 175, 0.2)",
    background: mode === "dark"
      ? "linear-gradient(135deg, #2A2A3C 0%, #1a1a2e 100%)"
      : mode === "custom"
        ? `linear-gradient(135deg, ${customColors?.background || "#ffffff"} 0%, ${customColors?.cardBg || "#f8f9fa"} 100%)`
        : "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
    boxShadow: mode === "dark"
      ? "0 2px 8px rgba(0, 0, 0, 0.3)"
      : "0 2px 12px rgba(30, 139, 175, 0.08)",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    '&:hover': { 
      transform: "translateY(-2px) scale(1.05)",
      boxShadow: mode === "dark"
        ? "0 8px 24px rgba(30, 139, 175, 0.3)"
        : "0 8px 24px rgba(30, 139, 175, 0.2)",
      borderColor: "rgba(30, 139, 175, 0.4)",
    }
  }}
>
    <ChevronRightIcon />
  </IconButton>
      </Box>
      </Box>

      {!selectedPanel && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            textAlign: "center",
            px: 3,
          }}
        >
          <Box
            sx={{
              mb: 3,
              opacity: 0.7,
            }}
          >
            <GroupIcon sx={{ fontSize: 80, color: "#1e8bafff" }} />
          </Box>
          <Typography
            variant="h4"
            sx={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: 700,
              mb: 2,
            }}
          >
            Welcome to Control Panel
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: mode === "dark"
                ? palette.dark.text
                : mode === "custom"
                  ? customColors?.text || "#111111"
                  : palette.light.text,
              maxWidth: 600,
              mb: 4,
              opacity: 0.8,
            }}
          >
            Select an option from above to manage roles, permissions, and users.
            Create custom roles, register new users, or view your account details.
          </Typography>
        </Box>
      )}

      {selectedPanel === "lists" && (
        <ListsPanel
          mode={mode}
          customColors={customColors}
          setSnackbar={setSnackbar}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          currentUserRole={currentUserRole}
          myPermissions={myPermissions}
        />
      )}

      {selectedPanel === "manage_folders" && (
  <ManageFoldersPanel
    mode={mode}
    customColors={customColors}
    setSnackbar={setSnackbar}
    isLoading={isLoading}
    setIsLoading={setIsLoading}
    currentUserRole={currentUserRole}
  />
)}


      {selectedPanel === "classes" && (
  <ClassPanel
    mode={mode}
    customColors={customColors}
    setSnackbar={setSnackbar}
    isLoading={isLoading}
    setIsLoading={setIsLoading}
    currentUserRole={currentUserRole}
  />
)}


      {selectedPanel === "permissions_for_custom_role" && (
        <SetPermissionsPanel
          mode={mode}
          customColors={customColors}
          setSnackbar={setSnackbar}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          currentUserRole={currentUserRole}
          myPermissions={myPermissions}
        />
      )}

      {selectedPanel === "create_custom_role" && (
        <CreateCustomRolePanel
          mode={mode}
          customColors={customColors}
          setSnackbar={setSnackbar}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      )}

      {selectedPanel === "register_user" && (
        <RegisterUserPanel
          mode={mode}
          customColors={customColors}
          setSnackbar={setSnackbar}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          currentUserRole={currentUserRole}
          myPermissions={myPermissions}
          customRolesDropdown={customRoles}
        />
      )}

      {selectedPanel === "my_permissions_custom" && (
        <MyPermissionsPanel
          mode={mode}
          customColors={customColors}
          setSnackbar={setSnackbar}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          currentUserRole={currentUserRole}
        />
      )}

      {selectedPanel === "my_details" && (
        <MyDetailsPanel
          mode={mode}
          customColors={customColors}
          setSnackbar={setSnackbar}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          currentUserRole={currentUserRole}
          customRolesDropdown={customRoles}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
